import React, { ReactNode } from "react";
import { createPortal } from "react-dom";
import { ArrowRight, X } from "lucide-react";
import { Button } from "components";
import type { LucideIcon } from "lucide-react";
import "./styles.css";

export interface ConfirmDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  /** Message body - string or ReactNode for multiple paragraphs */
  message: ReactNode;
  cancelLabel?: string;
  confirmLabel?: string;
  /** Icon shown on the confirm button (e.g. ArrowRight) */
  confirmIcon?: LucideIcon;
  isSubmitting?: boolean;
  /** Disable the confirm button without triggering loading state */
  confirmDisabled?: boolean;
}

const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  cancelLabel = "No, go back",
  confirmLabel = "Confirm",
  confirmIcon: ConfirmIcon = ArrowRight,
  isSubmitting = false,
  confirmDisabled = false,
}) => {
  if (!isOpen) return null;

  const modalContent = (
    <div className="confirm-dialog-overlay" onClick={onClose}>
      <div className="confirm-dialog" onClick={(e) => e.stopPropagation()}>
        <button
          type="button"
          className="confirm-dialog-close"
          onClick={onClose}
          disabled={isSubmitting}
          aria-label="Close"
        >
          <X size={20} />
        </button>
        <div className="confirm-dialog-content">
          <h3 className="confirm-dialog-title">{title}</h3>
          <div className="confirm-dialog-message">
            {typeof message === "string" ? <p>{message}</p> : message}
          </div>
        </div>

        <div className="confirm-dialog-footer">
          <Button
            variant="ghost"
            size="small"
            onClick={onClose}
            disabled={isSubmitting}
            backgroundColor="#F3F3F3"
            textColor="#121212"
            className="confirm-dialog-btn"
          >
            {cancelLabel}
          </Button>
          <Button
            variant="primary"
            size="small"
            onClick={onConfirm}
            disabled={isSubmitting || confirmDisabled}
            loading={isSubmitting}
            backgroundColor="#121212"
            textColor="#ffffff"
            icon={ConfirmIcon}
            iconPosition="right"
            iconColor="#ffffff"
            className="confirm-dialog-btn"
          >
            {confirmLabel}
          </Button>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default ConfirmDialog;
